import React from 'react';
import {Map} from 'immutable';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {PAYPAL_BUTTON_ELEMENT_INSTANCE} from '@packages/systems/commerce/constants';
import {CommerceCartOrCheckoutFormConstraint} from '@packages/systems/core/constants/SharedConstraintRules';
import {reportBugOnce} from '@packages/utilities/reporter';
import {
  withMeta,
  withSystem,
  Plugins,
  Atom,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  TRecord,
  TEnum,
  TTypeReference,
  TTypeConstructor,
  TBoolean,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {getInPojo, getRecord} from '@packages/utilities/data-utils';
import isEqual from 'lodash/isEqual';

const stylePositionRelative = Map({style: Map({position: 'relative'})});

const getElementWindow = (element?: Element | null) => {
  if (!element) {
    return null;
  }

  const doc = element.ownerDocument;
  return doc.defaultView;
};

const maskStyles = (isPreviewMode: boolean) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  zIndex: 999,
  cursor: isPreviewMode ? 'pointer' : 'auto',
});

const headScript =
  'https://www.paypal.com/sdk/js?client-id=sb&merchant-id=XXX&commit=false';

const CommercePayPalCheckoutButtonWrapper = (props: AtomRenderProps) => {
  const previewMode = usePreviewMode();
  const runtimeEnvironment = useRuntimeEnvironment();
  return (
    <CommercePayPalCheckoutButton
      {...props}
      previewMode={previewMode}
      runtimeEnvironment={runtimeEnvironment}
    />
  );
};

class CommercePayPalCheckoutButton extends React.Component<
  AtomRenderProps & {
    previewMode: boolean;
    runtimeEnvironment: Environment;
  }
> {
  elementRef = React.createRef();
  paypalButton: any = null;

  componentDidMount() {
    this.generatePayPalButton();
  }

  // @ts-expect-error - TS7031 - Binding element 'prevData' implicitly has an 'any' type. | TS7031 - Binding element 'prevEnvironment' implicitly has an 'any' type.
  componentDidUpdate(prevProps) {
    const {data: prevData, environmentContext: prevEnvironment} = prevProps;
    const {data, environmentContext} = this.props;
    if (
      !prevEnvironment.has('loadedHeadScripts') ||
      !environmentContext.has('loadedHeadScripts')
    ) {
      return;
    }

    const paypalFinishedLoading =
      !prevEnvironment.get('loadedHeadScripts').has(headScript) &&
      environmentContext.get('loadedHeadScripts').has(headScript);

    if (paypalFinishedLoading || !isEqual(data, prevData)) {
      this.updatePaypalButton();
    }
  }

  generatePayPalButton = () => {
    const {data} = this.props;
    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'Element | null | undefined'.
    const win = getElementWindow(this.elementRef.current);
    // @ts-expect-error - TS2551 - Property 'paypal' does not exist on type 'Window & typeof globalThis'. Did you mean 'Papa'?
    if (!win || !win.paypal) {
      return;
    }

    try {
      // @ts-expect-error - TS2551 - Property 'paypal' does not exist on type 'Window & typeof globalThis'. Did you mean 'Papa'?
      this.paypalButton = win.paypal.Buttons({
        style: getRecord(getInPojo(data, ['buttonStyle'])) ?? {},
      });
      this.paypalButton.render(this.elementRef.current);
      // internally, paypal uses a library called zoid to handle the iframe
      // that contains the paypal button. it handles a whole bunch of cross-domain
      // communication logic. within this is the idea that the channel between
      // the parent and iframe can be closed. when the channel is closed, the button
      // can no longer be used, and the iframe is destroyed. a channel close happens
      // whenever the zoid wrapping element updates somehow, such as it being moved
      // within the DOM tree. this event handler catches the first close event it
      // receives, and since the button is destroyed when it's closed, we just
      // generate a new button. we also use this to our advantage to handle updating
      // the paypal button's styles, by closing the button manually when there's a change
      // -----------
      // zoid: https://github.com/krakenjs/zoid
      // paypal checkout components: https://github.com/paypal/paypal-checkout-components
      this.paypalButton.event.once('zoid-close', this.generatePayPalButton);
    } catch (err: any) {
      reportBugOnce(
        'PayPalCheckoutButtonRenderingError',
        'A fatal error occurred when attempting to render the PayPal Button',
        {
          err,
          team: 'ecommerce',
          ticket: 'https://webflow.atlassian.net/browse/BUG-8306',
        }
      );
    }
  };

  updatePaypalButton = () => {
    if (!this.paypalButton) {
      this.generatePayPalButton();
    } else {
      // since a button's styles can't be updated after it's created
      // we use the close() function, which is caught in the zoid-close
      // event above, which then generates a new paypal button
      this.paypalButton.close();
    }
  };

  // we register this event on clicking the mask in preview mode, since if
  // you attempt to stop the event on the paypal button by firing an instant
  // reject on the `payment` method, the window will still pop up for a brief
  // second, giving a flash effect that's kind of unpleasant. as well, something
  // about the timing of that function will also cause an `alert` to flash in and
  // out rapidly, and then just disappear before it's able to be read.
  onMaskClick = () => {
    const isPreviewMode = this.props.previewMode;

    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'Element | null | undefined'.
    const win = getElementWindow(this.elementRef.current);
    if (!isPreviewMode || !win) {
      return;
    }

    win.alert('PayPal Checkout is not available in preview mode.');
  };

  render() {
    const {attributes, classes, data, generateAttributes} = this.props;
    const isPreviewMode = this.props.previewMode;
    const runtimeEnvironment = this.props.runtimeEnvironment;
    const isPublishedSite = runtimeEnvironment === WebflowEnvironments.SSR;

    const buttonStyles = getRecord(getInPojo(data, ['buttonStyle'])) ?? {};

    const styledAttributes = isPublishedSite
      ? attributes
      : attributes.mergeDeep(stylePositionRelative);

    const stampedAttributes = styledAttributes.set(
      PAYPAL_BUTTON_ELEMENT_INSTANCE,
      JSON.stringify(buttonStyles)
    );

    return (
      <div
        {...generateAttributes(stampedAttributes, classes)}
        // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'.
        ref={this.elementRef}
      >
        {isPublishedSite ? null : (
          // TODO: fix jsx-a11y lint error

          // @ts-expect-error - TS2322 - Type '{ position: string; top: number; left: number; width: string; height: string; opacity: number; zIndex: number; cursor: string; }' is not assignable to type 'Properties<string | number, string & {}>'.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div style={maskStyles(isPreviewMode)} onClick={this.onMaskClick} />
        )}
      </div>
    );
  }
}

const config = createIntegratedAtom({
  displayName: 'PayPal',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Commerce'), iconName('Currency')),
  },
  headScript,
  showEmpty: false,
  constraints: {
    ancestors: [
      // @ts-expect-error - TS2322 - Type '{ readonly is: readonly ["CommerceCartForm", "CommerceCheckoutFormContainer"]; readonly name: "Cart or Checkout Form"; readonly rule: "ExactlyOne"; }' is not assignable to type 'RelationshipConstraint'.
      CommerceCartOrCheckoutFormConstraint,
      {is: 'DynamoWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutOrderItemsList', rule: 'Forbid'},
      {is: 'CommerceCheckoutShippingMethodsList', rule: 'Forbid'},
      {is: 'CommerceCartList', rule: 'Forbid'},
      {is: 'CommerceCartQuickCheckoutActions', rule: 'Forbid'},
    ],
  },
  // `displayGrid` is not part of `WebflowCssProperties`, but is used by `StylePanelLayoutSection`
  // to disable one specific display type

  lockedStyles: {
    // @ts-expect-error - TS2322 - Type '{ displayGrid: { when: true; message: string; }; displayFlex: { when: true; message: string; }; }' is not assignable to type 'Partial<Record<keyof WebflowCssProperties, LockedStyleType>>'.
    displayGrid: {
      when: true,
      message:
        'The PayPal Checkout button is not compatible with Grid layouts.',
    },
    displayFlex: {
      when: true,
      message:
        'The PayPal Checkout button is not compatible with Flex layouts.',
    },
  },
  render: Atom.createRender(CommercePayPalCheckoutButtonWrapper),
  fields: {
    buttonStyle: TRecord(
      {
        tagline: withSystem({private: true}, TBoolean),
        layout: withSystem(
          {
            label: 'Layout',
            defaultValue: 'vertical',
          },
          TEnum({
            vertical: 'Vertical',
            horizontal: 'Horizontal',
          })
        ),
        color: withMeta(
          {
            variant: 'menu',
            withoutLivePreview: true,
          },
          withSystem(
            {
              label: 'Color',
              defaultValue: 'gold',
            },
            TEnum({
              gold: 'Gold',
              blue: 'Blue',
              silver: 'Silver',
              white: 'White',
              black: 'Black',
            })
          )
        ),
        shape: withSystem(
          {
            label: 'Shape',
            defaultValue: 'rect',
          },
          TEnum({
            rect: 'Rectangular',
            pill: 'Pill',
          })
        ),
        label: withMeta(
          {
            variant: 'menu',
            withoutLivePreview: true,
          },
          withSystem(
            {
              label: 'Label',
              defaultValue: 'paypal',
            },
            TEnum({
              paypal: 'PayPal',
              checkout: 'PayPal Checkout',
              buynow: 'Buy Now',
              pay: 'Pay with PayPal',
            })
          )
        ),
        note: withMeta(
          {
            content:
              'PayPal will be hidden for subscription products, which can only be purchased with Stripe.',
            universityLink:
              'https://university.webflow.com/integrations/paypal-payments',
          },
          withSystem(
            {defaultValue: false},
            TTypeConstructor(
              qualify(Plugins.name('Commerce'), typeName('DismissibleNote'))
            )
          )
        ),
      },
      TTypeReference('extraButtonStyles')
    ),
  },
});

export default config;
